import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import React, { Fragment } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { DialogEnum, useDialog } from "../../contexts/DialogContext";
import { LoginFormValues, useLoginForm } from "../../forms/auth/login-form";
import Close from "../icons/close";

export default function LoginDialog() {
  const { signIn, notConfirmedAccount } = useAuth();
  const dialog = useDialog();
  const navigate = useNavigate();
  const { register, handleSubmit, formState, reset } = useLoginForm();

  const closeDialog = () => dialog.pop();

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await signIn(values);
      closeDialog();
    } catch (e) {
      toast.error(e as string);
      if (e === "User is not confirmed.") {
        notConfirmedAccount(values.email);
        dialog.push(DialogEnum.CONFIRM_REGISTRATION);
      }
    }
  };

  const createAccount = () => {
    closeDialog();
    navigate("/account/create");
  };

  return (
    <Transition
      appear
      show={dialog.isOpened(DialogEnum.SIGN_IN)}
      as={Fragment}
      afterLeave={() => reset({ email: "", password: "" })}
    >
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        onClose={closeDialog}
      >
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-700"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 transition-opacity bg-black bg-opacity-30" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="w-[400px] h-[400px]">
              <div className="relative max-w-md mx-auto bg-white rounded-md shadow-md">
                <div className="flex justify-end pt-4 pr-4">
                  <button className="outline-none" onClick={closeDialog}>
                    <Close className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-10 pt-5">
                  <Dialog.Title className="text-2xl text-primary-blue mb-2 font-semibold tracking-[0.07rem]">
                    LOG IN
                  </Dialog.Title>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mt-5 mb-[30px] flex flex-col gap-2.5">
                      <input
                        type="email"
                        placeholder="Email"
                        autoComplete="email"
                        {...register("email")}
                        className={classNames(
                          "w-full bg-white block px-[15px] py-[12px] border border-primary-blue rounded-md focus:outline-none focus:ring-1 focus:ring-primary-blue",
                          {
                            "border-red-500 text-red-600 focus:border-red-500 focus:ring-red-500":
                              !!formState?.errors?.email,
                          }
                        )}
                      />
                      <div className="-mt-1 text-sm text-red-500">
                        {formState?.errors?.email?.message}
                      </div>
                      <input
                        type="password"
                        placeholder="Password"
                        autoComplete="current-password"
                        {...register("password")}
                        className={classNames(
                          "w-full bg-white block px-[15px] py-[12px] border border-primary-blue rounded-md focus:outline-none focus:ring-1 focus:ring-primary-blue",
                          {
                            "border-red-500 text-red-600 focus:border-red-500 focus:ring-red-500":
                              !!formState?.errors?.password,
                          }
                        )}
                      />
                      <div className="-mt-1 text-sm text-red-500">
                        {formState?.errors?.password?.message}
                      </div>
                      <button
                        className="w-full inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        type="submit"
                      >
                        LOG IN
                      </button>
                    </div>
                  </form>
                  <div className="text-center">
                    <button
                      className="font-semibold text-primary-blue"
                      onClick={() => dialog.push(DialogEnum.FORGOT_PASSWORD)}
                    >
                      Forgot password?
                    </button>
                    <div className="mt-5">
                      <span className="font-normal text-gray-500">
                        No account?
                      </span>
                      <button
                        className="ml-2 font-semibold text-primary-blue"
                        onClick={createAccount}
                      >
                        Create One
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
